import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import {
  subscribeLifetimeFreeTenants,
  type LifetimeFreeListResult,
} from '@/lib/firestoreTenantDetail';

/**
 * Admin `/billing/promotions` — **só as isenções (Lifetime Free)** desde a
 * #679 F4. Cupons e promo codes foram aposentados (9.17): com preço por tenant
 * na venda assistida, cupom é desconto em dobro, e o Checkout da venda nasce com
 * `allow_promotion_codes: false`. Desde a F7 (#738) a lista é das cortesias
 * por tipo comercial (`commercial_mode: COURTESY`, mais as legadas só com
 * cupom até a E1 — `subscribeLifetimeFreeTenants`); demo não entra. O path
 * continua o mesmo porque o cartão do painel financeiro (`LifetimeFreeCard`)
 * aponta para cá.
 * Spec: docs/screens/billing-promotions.md
 *
 * Origem: sem issue — commit 4c08bb698, 2026-05-26 (Sprint 35 PR G2, CRUD de
 * cupons e promo codes; hoje só a lista de isenções).
 *
 * Usado por: rota `billing/promotions` (`routes/router.tsx`), menu Cobrança
 * (`AdminLayout`); link do `LifetimeFreeCard` do painel financeiro.
 *
 * Dados: collection group `billing` (`tenants/{tid}/billing/subscription`),
 * duas consultas em `subscribeLifetimeFreeTenants`
 * (`lib/firestoreTenantDetail.ts`): `commercial_mode == COURTESY` ordenado por
 * `courtesy.applied_at desc` e o legado `discount.is_lifetime_free == true` por
 * `discount.lifetime_free_applied_at desc`; rule `match
 * /{path=**}/billing/{billingDoc}` (só super-admin). Só leitura — aplicar ou
 * remover a isenção é no detalhe do tenant.
 */
export function PromotionsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [lifetime, setLifetime] = useState<LifetimeFreeListResult>({ kind: 'LOADING' });

  useEffect(() => {
    setLifetime({ kind: 'LOADING' });
    return subscribeLifetimeFreeTenants(setLifetime);
  }, []);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('promotions.subtitle')}>
          {t('promotions.tab.lifetime')}
        </Header>
      }
    >
      <LifetimeTab state={lifetime} />
    </ContentLayout>
  );
}

/** Lista de cortesias: um card por tenant com categoria, quando, por quem e motivo. */
function LifetimeTab({ state }: { state: LifetimeFreeListResult }): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (state.kind === 'LOADING') return <Spinner />;
  if (state.kind === 'ERROR') return <Alert type="error">{t('promotions.errorLoad')}</Alert>;
  if (state.rows.length === 0) {
    return (
      <Container>
        <Box textAlign="center" color="text-body-secondary" padding="l">
          {t('promotions.lifetime.empty')}
        </Box>
      </Container>
    );
  }
  return (
    <SpaceBetween size="s">
      {state.rows.map((row) => (
        <Container
          key={row.tenantId}
          header={
            <Header
              variant="h3"
              actions={
                <Button
                  onClick={(): void => {
                    void navigate(`/tenants/${row.tenantId}`);
                  }}
                >
                  {t('promotions.lifetime.openTenant')}
                </Button>
              }
            >
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <span style={{ fontFamily: 'monospace' }}>{row.tenantId}</span>
                <Badge color="grey">
                  {t(`promotions.lifetime.category.${row.category}`, { defaultValue: row.category })}
                </Badge>
              </SpaceBetween>
            </Header>
          }
        >
          <SpaceBetween size="xxs">
            <Box fontSize="body-s" color="text-status-inactive">
              {t('promotions.lifetime.appliedAt')}: {new Date(row.appliedAt).toLocaleString()}
              {row.appliedBy != null && ` · ${t('promotions.lifetime.appliedBy')}: ${row.appliedBy}`}
            </Box>
            {row.reason != null && (
              <Box fontSize="body-s" color="text-status-inactive">{row.reason}</Box>
            )}
          </SpaceBetween>
        </Container>
      ))}
    </SpaceBetween>
  );
}
